import { Injectable, inject } from '@angular/core';

import { ConfirmService } from '@core/services/confirm.service';
import { ToastService } from '@core/services/toast.service';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';

import {
  ArchiveSessionRequest,
  EndSessionRequest,
  PauseSessionRequest,
  ResetArcherRequest,
  ResumeSessionRequest,
  SetEndsRequest
} from '../models/coach-scoring-live.request';

@Injectable({ providedIn: 'root' })
export class CoachScoringLiveService {
  private readonly state = inject(BrijjSportsStateService);
  private readonly toast = inject(ToastService);
  private readonly confirm = inject(ConfirmService);

  setEnds(request: SetEndsRequest): void {
    // Real-API replacement:
    //   await firstValueFrom(this.http.put(`/coach/scoring/${request.sessionId}/archers/${request.athleteId}`, request));
    this.state.setArcherRoundEnds(request.sessionId, request.athleteId, request.roundIndex, request.ends);
  }

  pause(request: PauseSessionRequest): void {
    this.state.pauseSession(request.sessionId);
  }

  resume(request: ResumeSessionRequest): void {
    this.state.resumeSession(request.sessionId);
  }

  async confirmAndEnd(request: EndSessionRequest, sessionName?: string): Promise<boolean> {
    const ok = await this.confirm.ask({
      title: 'End this session?',
      message: 'The session will be marked completed and scores locked.',
      confirmLabel: 'End session',
      variant: 'warn'
    });
    if (!ok) return false;
    this.state.endSession(request.sessionId);
    this.toast.success('Session ended', sessionName ?? '');
    return true;
  }

  async confirmAndArchive(request: ArchiveSessionRequest): Promise<boolean> {
    const ok = await this.confirm.ask({
      title: 'Archive this session?',
      message: 'Archived sessions stay searchable but no longer show as active.',
      confirmLabel: 'Archive'
    });
    if (!ok) return false;
    this.state.archiveSession(request.sessionId);
    return true;
  }

  async confirmAndResetArcher(request: ResetArcherRequest): Promise<boolean> {
    const ok = await this.confirm.ask({
      title: 'Reset this archer?',
      message: 'All recorded arrows in every round for this archer will be cleared.',
      confirmLabel: 'Reset',
      variant: 'warn'
    });
    if (!ok) return false;
    this.state.resetParticipantScore(request.sessionId, request.athleteId);
    return true;
  }
}
